import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Table,
  Steps,
} from 'antd';
import {
  DeleteOutlined,
  ArrowLeftOutlined,
  StopOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getWorkflowInstance,
  deleteWorkflowInstance,
  cancelWorkflowInstance,
  executeWorkflowStep,
  type WorkflowInstance,
  type WorkflowStepResult,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const INSTANCE_STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

const STEP_STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'green',
  failed: 'red',
  skipped: 'orange',
};

export default function WorkflowInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission('workflows', 'write');
  const canDelete = hasPermission('workflows', 'delete');

  const [instance, setInstance] = useState<WorkflowInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInstance = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getWorkflowInstance(id)
      .then((inst) => setInstance(inst))
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    loadInstance();
  }, [loadInstance]);

  const handleCancel = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('workflows.confirm_cancel'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          const updated = await cancelWorkflowInstance(id);
          setInstance(updated);
          message.success(t('workflows.instance_cancel_success'));
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('workflows.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteWorkflowInstance(id);
          message.success(t('workflows.instance_delete_success'));
          navigate('/workflows/instances');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handleExecuteStep = useCallback(async () => {
    if (!id) return;
    try {
      const updated = await executeWorkflowStep(id);
      setInstance(updated);
      message.success(t('workflows.step_execute_success'));
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t]);

  const stepResultColumns: ColumnsType<WorkflowStepResult> = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'step_index',
        key: 'step_index',
        width: 60,
        align: 'center',
      },
      {
        title: t('workflows.field.name'),
        dataIndex: 'step_name',
        key: 'step_name',
        ellipsis: true,
      },
      {
        title: t('workflows.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STEP_STATUS_COLORS[status] ?? 'default'}>
            {t(`workflows.step_status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.started_at'),
        dataIndex: 'started_at',
        key: 'started_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.completed_at'),
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.error'),
        dataIndex: 'error',
        key: 'error',
        ellipsis: true,
        render: (val: string) => (val ? <Tag color="red">{val}</Tag> : '-'),
      },
    ],
    [t]
  );

  const isRunnable =
    instance?.status === 'running' || instance?.status === 'pending';
  const isCancellable =
    instance?.status === 'pending' || instance?.status === 'running';
  const isDeletable =
    instance?.status === 'completed' ||
    instance?.status === 'failed' ||
    instance?.status === 'cancelled';

  // Steps progress indicator
  const stepsStatus = useMemo(() => {
    if (!instance?.step_results?.length) return [];
    return instance.step_results.map((sr) => {
      let status: 'wait' | 'process' | 'finish' | 'error' = 'wait';
      if (sr.status === 'completed') status = 'finish';
      else if (sr.status === 'running') status = 'process';
      else if (sr.status === 'failed') status = 'error';
      else if (sr.status === 'skipped') status = 'wait';
      return { title: sr.step_name, status };
    });
  }, [instance]);

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error || !instance) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows/instances')}
        >
          {t('app.back')}
        </Button>
        <Alert
          type="error"
          message={error || t('app.not_found')}
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows/instances')}
        >
          {t('app.back')}
        </Button>
        {canWrite && isRunnable && (
          <Button
            type="primary"
            icon={<CaretRightOutlined />}
            onClick={handleExecuteStep}
          >
            {t('workflows.action.execute_step')}
          </Button>
        )}
        {canWrite && isCancellable && (
          <Button
            danger
            icon={<StopOutlined />}
            onClick={handleCancel}
          >
            {t('workflows.action.cancel')}
          </Button>
        )}
        {canDelete && isDeletable && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>
        {t('workflows.instance_detail_title')}: {instance.definition_name}
      </h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('workflows.field.definition_name')}>
          <a onClick={() => navigate(`/workflows/definitions/${instance.definition_id}`)}>
            {instance.definition_name}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.status')}>
          <Tag color={INSTANCE_STATUS_COLORS[instance.status] ?? 'default'}>
            {t(`workflows.instance_status_options.${instance.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.current_step_index')}>
          {instance.current_step_index}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.error')}>
          {instance.error ? <Tag color="red">{instance.error}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.started_at')}>
          {instance.started_at ? new Date(instance.started_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.completed_at')}>
          {instance.completed_at ? new Date(instance.completed_at).toLocaleString() : '-'}
        </Descriptions.Item>

        {/* Audit */}
        <Descriptions.Item label={t('workflows.field.created_by')}>
          {instance.created_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.created_at')}>
          {instance.created_at ? new Date(instance.created_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Step Progress */}
      {stepsStatus.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3>{t('workflows.step_progress')}</h3>
          <Steps
            current={instance.current_step_index}
            items={stepsStatus.map((s) => ({
              title: s.title,
              status: s.status,
            }))}
            size="small"
            style={{ marginBottom: 16 }}
          />
        </div>
      )}

      {/* Step Results Table */}
      <h3>{t('workflows.field.step_results')}</h3>
      <Table<WorkflowStepResult>
        columns={stepResultColumns}
        dataSource={instance.step_results ?? []}
        rowKey="step_index"
        pagination={false}
        size="small"
      />
    </div>
  );
}
